import { useEffect, useState } from "react";
import { ChartBar, Trophy, Sparkle, CircleNotch, FilmSlate, CheckCircle, LinkSimple } from "@phosphor-icons/react";
import { toast } from "sonner";
import client from "../lib/api";

const money = (n) => `$${Number(n).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;

export const SourceRoiCard = () => {
  const [data, setData] = useState(null);
  const [ideas, setIdeas] = useState([]);
  const [ideasBusy, setIdeasBusy] = useState(false);

  useEffect(() => {
    client.get("/admin/source-roi").then((r) => setData(r.data)).catch(() => {});
    client.get("/admin/angle-ideas/latest").then((r) => setIdeas(r.data?.ideas || [])).catch(() => {});
  }, []);

  const generateIdeas = async () => {
    setIdeasBusy(true);
    try {
      const r = await client.post("/admin/angle-ideas/generate");
      setIdeas(r.data?.ideas || []);
    } catch { /* keep old ideas */ }
    finally { setIdeasBusy(false); }
  };

  const [rendering, setRendering] = useState({});
  const renderIdea = async (angle) => {
    setRendering((p) => ({ ...p, [angle]: true }));
    try {
      await client.post("/admin/angle-ideas/render", { angle });
      setIdeas((prev) => prev.map((i) => (i.angle === angle ? { ...i, rendered_at: new Date().toISOString() } : i)));
    } catch { /* leave button */ }
    finally { setRendering((p) => ({ ...p, [angle]: false })); }
  };

  if (!data) return null;
  const hasAnything = (data.rows || []).length > 0 || data.spend_total > 0 || ideas.length > 0;
  if (!hasAnything) return null;

  return (
    <div className="mt-6 card-surface p-4" data-testid="source-roi-card">
      <div className="flex items-center gap-2 mb-3">
        <ChartBar size={14} weight="bold" color="#FF9764" />
        <span className="overline">AD SOURCE ROI · COST PER SIGNUP · LAST 30 DAYS</span>
        <span className="ml-auto text-[10px] font-mono text-neutral-400" data-testid="source-roi-totals">
          {money(data.spend_total)} spent · {data.paid_signups} paid signup{data.paid_signups === 1 ? "" : "s"}
          {data.cost_per_signup != null && ` · ${money(data.cost_per_signup)}/signup`}
        </span>
      </div>

      {data.winner && (
        <div className="mb-3 flex items-center gap-2 rounded-lg border border-[#FF5A1F]/40 bg-[#FF5A1F]/10 px-3 py-2"
             data-testid="source-roi-winner">
          <Trophy size={14} weight="fill" color="#FF9764" />
          <span className="text-[11px] text-white">
            <b className="text-[#FF9764]">Winning source:</b> {data.winner.angle} at{" "}
            <b>{money(data.winner.cost_per_signup)}/signup</b>
            <span className="text-neutral-400"> — shift budget here</span>
          </span>
        </div>
      )}

      {data.rows.length > 0 ? (
        <div className="space-y-1.5">
          {data.rows.map((r) => (
            <div key={r.angle} data-testid={`source-roi-row-${r.angle}`}
                 className={`flex items-center gap-3 rounded-lg px-3 py-2 ${
                   r.is_winner
                     ? "bg-[#FF5A1F]/[0.08] border border-[#FF5A1F]/30"
                     : "bg-white/[0.03]"
                 }`}>
              <span className="min-w-0 flex-1 text-[12px] font-bold text-white truncate flex items-center gap-1.5" title={r.angle}>
                {r.is_winner && <Trophy size={12} weight="fill" color="#FF9764" data-testid="source-roi-winner-crown" />}
                {r.angle}
              </span>
              <span className="shrink-0 w-20 text-right text-[11px] font-mono text-neutral-300">
                {r.signups} signup{r.signups === 1 ? "" : "s"}
              </span>
              <span className="shrink-0 w-20 text-right text-[11px] font-mono text-neutral-400">
                {r.spend > 0 ? money(r.spend) : "—"}
              </span>
              <span className={`shrink-0 w-24 text-right text-[11px] font-mono font-bold ${
                r.cost_per_signup != null ? "text-[#FF9764]" : "text-neutral-600"
              }`}>
                {r.cost_per_signup != null ? `${money(r.cost_per_signup)}/signup` : "no spend tagged"}
              </span>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-[11px] text-neutral-500">
          Spend is logged but no paid signups landed yet — chips appear as soon as an ad brings one in.
        </div>
      )}

      <div className="text-[10px] text-neutral-500 mt-3">
        Tag spend with an angle (Content Studio → Log FB spend) to see which Facebook angle earns its budget.
        {data.organic_signups > 0 && ` ${data.organic_signups} more signup${data.organic_signups === 1 ? "" : "s"} came in free (reels/direct).`}
      </div>

      <div className="mt-3 border-t border-white/5 pt-3" data-testid="angle-ideas-section">
        <div className="flex items-center gap-2">
          <span className="overline">FRESH ANGLE IDEAS · AI</span>
          <button
            type="button"
            onClick={generateIdeas}
            disabled={ideasBusy}
            data-testid="angle-ideas-generate-btn"
            className="ml-auto inline-flex items-center gap-1 rounded-full bg-[#FF5A1F]/15 hover:bg-[#FF5A1F]/25 border border-[#FF5A1F]/40 px-2.5 py-1 text-[10px] font-bold text-[#FF9764] transition-colors disabled:opacity-50"
          >
            {ideasBusy ? <CircleNotch size={11} className="animate-spin" /> : <Sparkle size={11} weight="fill" />}
            {ideasBusy ? "Thinking…" : ideas.length ? "Suggest new batch" : "Suggest fresh angles"}
          </button>
        </div>
        {ideas.length > 0 && (
          <div className="mt-2 space-y-1.5">
            {ideas.map((i) => (
              <div key={i.angle} data-testid={`angle-idea-${i.angle}`}
                   className="rounded-lg bg-white/[0.03] px-3 py-2">
                <div className="flex items-center gap-2">
                  <span className="text-[11px] font-mono font-bold text-[#FF9764]">{i.angle}</span>
                  <span className="ml-auto flex items-center gap-1.5">
                    {i.ad_link && (
                      <button
                        type="button"
                        onClick={() => {
                          navigator.clipboard.writeText(i.ad_link);
                          toast.success("Ad link copied — paste it as the ad's destination URL");
                        }}
                        data-testid={`angle-idea-adlink-${i.angle}`}
                        title={i.ad_link}
                        className="inline-flex items-center gap-1 rounded-full bg-white/5 hover:bg-white/15 border border-white/15 px-2.5 py-1 text-[10px] font-bold text-neutral-300 transition-colors"
                      >
                        <LinkSimple size={11} weight="bold" />
                        Copy ad link
                      </button>
                    )}
                    {i.rendered_at ? (
                      <span className="inline-flex items-center gap-1 text-[10px] font-bold text-emerald-400"
                            data-testid={`angle-idea-rendered-${i.angle}`}>
                        <CheckCircle size={11} weight="fill" /> Reel rendering — see Reel Library in ~2 min
                      </span>
                    ) : (
                      <button
                        type="button"
                        onClick={() => renderIdea(i.angle)}
                        disabled={!!rendering[i.angle]}
                        data-testid={`angle-idea-render-${i.angle}`}
                        className="inline-flex items-center gap-1 rounded-full bg-white/5 hover:bg-white/15 border border-white/15 px-2.5 py-1 text-[10px] font-bold text-white transition-colors disabled:opacity-50"
                      >
                        {rendering[i.angle] ? <CircleNotch size={11} className="animate-spin" /> : <FilmSlate size={11} weight="fill" />}
                        Make this reel
                      </button>
                    )}
                  </span>
                </div>
                <div className="text-[12px] font-bold text-white mt-0.5">"{i.hook}"</div>
                <div className="text-[10px] text-neutral-400 mt-0.5">{i.why}</div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
